import React, { useState } from "react";
import { Share2, Link2, Check, MessageCircle, Send } from "lucide-react";

const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z" />
  </svg>
);

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

export default function ShareButtons({
  url,
  title = "",
  description = "",
  hashtags = "",
  compact = false,
  label = "Compartir",
}) {
  const [copied, setCopied] = useState(false);

  const absoluteUrl = typeof window !== "undefined" && url && !/^https?:\/\//.test(url)
    ? `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`
    : url;

  const text = `${title}${description ? ` — ${description}` : ""}`.trim();
  const encodedUrl = encodeURIComponent(absoluteUrl || "");
  const encodedText = encodeURIComponent(text);
  const encodedTitle = encodeURIComponent(title);
  const encodedHashtags = encodeURIComponent(hashtags);

  const channels = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      Icon: MessageCircle,
      color: "bg-[#25D366] hover:bg-[#1ebd5a]",
    },
    {
      id: "telegram",
      label: "Telegram",
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      Icon: Send,
      color: "bg-[#26A5E4] hover:bg-[#1f8fcb]",
    },
    {
      id: "twitter",
      label: "X",
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}${hashtags ? `&hashtags=${encodedHashtags}` : ""}`,
      Icon: XIcon,
      color: "bg-black hover:bg-neutral-800",
    },
    {
      id: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      Icon: FacebookIcon,
      color: "bg-[#1877F2] hover:bg-[#1666d4]",
    },
  ];

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: absoluteUrl });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={async () => {
          const ok = await nativeShare();
          if (!ok) copyLink();
        }}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        data-testid="share-compact"
        aria-label="Compartir"
      >
        <Share2 className="w-4 h-4" />
        <span>{copied ? "Enlace copiado" : label}</span>
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mr-1">
        {label}:
      </span>
      {channels.map(({ id, label: l, href, Icon, color }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Compartir en ${l}`}
          title={`Compartir en ${l}`}
          data-testid={`share-${id}`}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-white transition-[background-color,transform] duration-fast ease-out-apple hover:-translate-y-0.5 active:scale-95 focus-ring ${color}`}
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button
        type="button"
        onClick={copyLink}
        aria-label={copied ? "Enlace copiado" : "Copiar enlace"}
        aria-live="polite"
        title="Copiar enlace"
        data-testid="share-copy"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-muted hover:bg-muted/70 text-foreground transition-[background-color,transform] duration-fast ease-out-apple active:scale-95 focus-ring"
      >
        <span className="relative w-4 h-4" aria-hidden="true">
          <Check
            className={`absolute inset-0 w-4 h-4 text-green-600 transition-[opacity,transform] duration-fast ease-out-apple ${
              copied ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          />
          <Link2
            className={`absolute inset-0 w-4 h-4 transition-[opacity,transform] duration-fast ease-out-apple ${
              copied ? "opacity-0 scale-75" : "opacity-100 scale-100"
            }`}
          />
        </span>
      </button>
    </div>
  );
}
